import React, { useState, useEffect, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import {
  Container,
  Row,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Button,
} from "reactstrap";
import ItemAnpr from "./item";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import PageSpinner from "../../components/Common/PageSpinner";
import styles from "./styles.module.css";

import {
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
} from "firebase/firestore";
import InfiniteScroll from "react-infinite-scroll-component";
import useDebounce from "../../components/useDebounce";
import { getVersionedCollection } from "../../utils/firestorePaths";

const RESULTS_PER_PAGE = 20;
// Limite de seguridad: cuantas paginas de Firestore leer como maximo por tanda
// mientras buscamos capturas con foto. Evita leer miles de docs si las capturas
// ANPR estan muy dispersas entre tickets normales. 20 paginas = hasta 400 docs.
const MAX_PAGES_PER_FETCH = 20;

const FILTERS = [
  { id: "all", label: "Todos", icon: "layers" },
  { id: "open", label: "Abiertos", icon: "directions_car" },
  { id: "closed", label: "Cerrados", icon: "check_circle" },
  { id: "canceled", label: "Cancelados", icon: "block" },
];

const useIsMobile = (bp = 768) => {
  const [m, setM] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setM(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return m;
};

const Anpr = (props) => {
  const isMobile = useIsMobile();
  const [searchPlate, setSearchPlate] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [filter, setFilter] = useState("all");
  const [tickets, setTickets] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const debouncedSearchPlate = useDebounce(searchPlate, 500);

  // Filtrar tickets abiertos de Redux cuando hay filtros activos
  const filteredOpenTickets = useMemo(() => {
    if (filter === "closed" || filter === "canceled") return [];

    let open = props.openedTickets.filter((t) => t.picture);

    if (searchDate) {
      const startOfDay = `${searchDate} 00:00:00.0000`;
      const endOfDay = `${searchDate} 23:59:59.0000`;
      open = open.filter(
        (t) => t.init_date >= startOfDay && t.init_date <= endOfDay
      );
    }

    if (debouncedSearchPlate && debouncedSearchPlate.length > 0) {
      const plateUpper = debouncedSearchPlate.trim().toUpperCase();
      open = open.filter(
        (t) => t.plate && t.plate.toUpperCase().startsWith(plateUpper)
      );
    }

    return open;
  }, [props.openedTickets, searchDate, debouncedSearchPlate, filter]);

  const fetchTickets = useCallback(
    async (reset = false) => {
      // Si el filtro es solo "open", no necesitamos query a Firestore
      if (filter === "open") {
        if (reset) setTickets([]);
        setHasMore(false);
        setLoading(false);
        return;
      }

      if (loading || (!hasMore && !reset)) return;

      setLoading(true);
      const ticketsRef = getVersionedCollection(
        props.currentCompany.id,
        "tickets",
        props.syncVersion
      );

      const constraints = [];
      const hasPlateSearch = debouncedSearchPlate && debouncedSearchPlate.length > 0;

      // Filtros de estado
      if (filter === "closed") {
        constraints.push(where("closed", "==", 1));
        constraints.push(where("canceled", "==", 0));
      } else if (filter === "canceled") {
        constraints.push(where("closed", "==", 1));
        constraints.push(where("canceled", "==", 1));
      } else {
        // "all": todos los cerrados de Firestore (abiertos vienen de Redux)
        constraints.push(where("closed", "==", 1));
      }

      if (hasPlateSearch) {
        const plateUpper = debouncedSearchPlate.trim().toUpperCase();
        constraints.push(where("plate", ">=", plateUpper));
        constraints.push(where("plate", "<=", plateUpper + "\uf8ff"));
        constraints.push(orderBy("plate"));
      } else if (searchDate) {
        const startOfDay = `${searchDate} 00:00:00.0000`;
        const endOfDay = `${searchDate} 23:59:59.0000`;
        constraints.push(orderBy("init_date", "desc"));
        constraints.push(where("init_date", ">=", startOfDay));
        constraints.push(where("init_date", "<=", endOfDay));
      } else {
        constraints.push(orderBy("init_date", "desc"));
      }

      try {
        // Firestore trae TODOS los tickets cerrados (con o sin foto ANPR), pero
        // esta pantalla solo muestra capturas (con foto). Como el limit() cuenta
        // tambien los tickets sin foto -que luego descartamos-, paginamos
        // internamente hasta juntar una pagina completa de capturas CON foto,
        // o hasta agotar los datos / el limite de seguridad de paginas.
        let cursor = reset ? null : lastVisible;
        let collected = [];
        let reachedEnd = false;
        let pages = 0;

        while (
          collected.length < RESULTS_PER_PAGE &&
          !reachedEnd &&
          pages < MAX_PAGES_PER_FETCH
        ) {
          const pageConstraints = [...constraints];
          if (cursor) pageConstraints.push(startAfter(cursor));
          pageConstraints.push(limit(RESULTS_PER_PAGE));

          const snapshot = await getDocs(query(ticketsRef, ...pageConstraints));
          pages++;

          if (snapshot.docs.length === 0) {
            reachedEnd = true;
            break;
          }
          cursor = snapshot.docs[snapshot.docs.length - 1];
          if (snapshot.docs.length < RESULTS_PER_PAGE) reachedEnd = true;

          let pageTickets = snapshot.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .filter((t) => t.picture); // solo capturas ANPR (con foto)

          // Si buscamos por placa + fecha, filtramos por fecha en el cliente
          if (hasPlateSearch && searchDate) {
            const startOfDay = `${searchDate} 00:00:00.0000`;
            const endOfDay = `${searchDate} 23:59:59.0000`;
            pageTickets = pageTickets.filter(
              (t) => t.init_date >= startOfDay && t.init_date <= endOfDay
            );
          }

          collected = collected.concat(pageTickets);
        }

        setLastVisible(cursor);
        setTickets((prev) => (reset ? collected : [...prev, ...collected]));
        setHasMore(!reachedEnd);
      } catch (err) {
        console.error("Error fetching tickets:", err, err?.message);
        if (reset) setTickets([]);
        setHasMore(false);
      }
      setLoading(false);
    },
    [debouncedSearchPlate, searchDate, filter, lastVisible, loading, hasMore, props.syncVersion]
  );

  useEffect(() => {
    setTickets([]);
    setLastVisible(null);
    setHasMore(true);
    fetchTickets(true);
  }, [props.currentCompany.id, searchDate, filter, debouncedSearchPlate, props.syncVersion]);

  const hasActiveFilters = searchPlate || searchDate || filter !== "all";
  const clearFilters = () => {
    setSearchPlate("");
    setSearchDate("");
    setFilter("all");
  };

  const showOpenSection = filter === "all" || filter === "open";

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="TicketControl" breadcrumbItem="Capturas" />

        {/* Filtros */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {/* Pills de estado */}
          {FILTERS.map((f) => {
            const isActive = filter === f.id;
            return (
              <button
                key={f.id}
                className={`btn btn-sm ${isActive ? "btn-primary" : "btn-light"}`}
                style={{
                  borderRadius: 20,
                  padding: isMobile ? "4px 10px" : "5px 14px",
                  fontSize: isMobile ? 12 : 14,
                  fontWeight: isActive ? 600 : 400,
                }}
                onClick={() => setFilter(f.id)}
              >
                <span className="material-symbols-rounded me-1" style={{ fontSize: 16 }}>{f.icon}</span>
                {f.label}
              </button>
            );
          })}

          <div className="vr d-none d-md-block" style={{ height: 24, alignSelf: "center" }}></div>

          {/* Fecha de ingreso */}
          <InputGroup size={isMobile ? "sm" : ""} style={{ width: "auto", minWidth: isMobile ? 150 : 210 }}>
            <InputGroupText style={{ backgroundColor: "#f8f9fa", border: "1px solid #ced4da", borderRight: 0, fontSize: 12, color: "#6c757d" }}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 16 }}>calendar_today</span>
              {!isMobile && "Ingreso"}
            </InputGroupText>
            <Input
              type="date"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
              style={{ fontSize: 14, borderLeft: 0 }}
            />
          </InputGroup>

          {/* Placa */}
          <InputGroup size={isMobile ? "sm" : ""} style={{ width: "auto", minWidth: isMobile ? 150 : 200 }}>
            <InputGroupText style={{ backgroundColor: "#f8f9fa", border: "1px solid #ced4da", borderRight: 0 }}>
              <span className="material-symbols-rounded text-muted" style={{ fontSize: 16 }}>search</span>
            </InputGroupText>
            <Input
              type="search"
              placeholder="Buscar matricula..."
              value={searchPlate}
              onChange={(e) => setSearchPlate(e.target.value)}
              style={{ fontSize: 14, borderLeft: 0 }}
            />
          </InputGroup>

          {/* Limpiar */}
          {hasActiveFilters && (
            <Button
              color="danger"
              outline
              size="sm"
              style={{ borderRadius: 20, fontSize: 12 }}
              onClick={clearFilters}
            >
              <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>close</span>Limpiar
            </Button>
          )}
        </div>

        {/* Referencias de estado */}
        <div className={styles.references} style={{ marginBottom: 16, fontSize: 12 }}>
          <div className={styles["reference-item"]}>
            <span className={`${styles["reference-bullet"]} ${styles["bullet-open"]}`}></span>
            <span className="text-muted">Abierto</span>
          </div>
          <div className={styles["reference-item"]}>
            <span className={`${styles["reference-bullet"]} ${styles["bullet-closed"]}`}></span>
            <span className="text-muted">Cerrado</span>
          </div>
          <div className={styles["reference-item"]}>
            <span className={`${styles["reference-bullet"]} ${styles["bullet-canceled"]}`}></span>
            <span className="text-muted">Cancelado</span>
          </div>
        </div>

        {/* Contenido */}
        <InfiniteScroll
          dataLength={tickets.length}
          next={fetchTickets}
          hasMore={hasMore}
          loader={
            tickets.length > 0 && (
              <div className="text-center text-muted py-3" style={{ fontSize: 13 }}>
                Cargando mas capturas...
              </div>
            )
          }
          style={{ overflowX: "hidden" }}
        >
          {/* Tickets abiertos (de Redux, filtrados) */}
          {showOpenSection && filteredOpenTickets.length > 0 && (
            <>
              <div className="text-muted mb-2 mt-2" style={{ fontSize: 13, borderBottom: "1px solid #dee2e6", paddingBottom: 6 }}>
                <span className="material-symbols-rounded me-1" style={{ fontSize: 16 }}>directions_car</span> Tickets Abiertos
              </div>
              <Row className="g-3">
                {filteredOpenTickets.map((ticket) => (
                  <Col key={ticket.id} md={4} lg={4} xl={3} xxl={2}>
                    <ItemAnpr ticket={ticket} country={props.currentCompany.country} />
                  </Col>
                ))}
              </Row>
            </>
          )}

          {/* Tickets cerrados de Firestore */}
          <Row className="g-3 mt-1">
            {(() => {
              let lastDate = "";
              return tickets.map((ticket) => {
                if (!ticket.picture) return null;
                const ticketDate = ticket.init_date || ticket.end_date;
                const currentDate = new Date(ticketDate).toLocaleDateString("es-ES");
                const showSeparator = currentDate !== lastDate;
                lastDate = currentDate;

                return (
                  <React.Fragment key={ticket.id}>
                    {showSeparator && (
                      <Col xs={12}>
                        <div className="text-muted mb-2 mt-3" style={{ fontSize: 13, borderBottom: "1px solid #dee2e6", paddingBottom: 6 }}>
                          <span className="material-symbols-rounded me-1" style={{ fontSize: 16 }}>calendar_today</span> {currentDate}
                        </div>
                      </Col>
                    )}
                    <Col md={4} lg={4} xl={3} xxl={2}>
                      <ItemAnpr ticket={ticket} country={props.currentCompany.country} />
                    </Col>
                  </React.Fragment>
                );
              });
            })()}
          </Row>
        </InfiniteScroll>

        {/* Empty / Loading */}
        {tickets.length === 0 && filteredOpenTickets.length === 0 && !loading && (
          <div className="text-center text-muted py-5">
            <span className="material-symbols-rounded d-block mb-3" style={{ fontSize: 48, opacity: 0.3 }}>photo_camera</span>
            No hay capturas
          </div>
        )}
        {loading && tickets.length === 0 && (
          <PageSpinner />
        )}
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  closedTickets: state.data.closedTickets,
  openedTickets: state.data.openedTickets,
  currentCompany: state.companies.currentCompany,
  syncVersion: state.data.syncVersion,
});

export default connect(mapStateToProps)(Anpr);
